import { Badge } from '@/shared/components/ui/Badge';
import { formatMoney, PRIORITY_TONES, STAGE_OPTIONS } from '../constants';
import type { Deal, DealStage } from '../types';

interface DealCardProps {
  deal: Deal;
  onOpen: (deal: Deal) => void;
  onStageChange: (deal: Deal, stage: DealStage) => void;
}

export function DealCard({ deal, onOpen, onStageChange }: DealCardProps) {
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => onOpen(deal)}
      onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && onOpen(deal)}
      className="flex cursor-pointer flex-col gap-2 rounded-card border border-outline bg-surface p-3 text-left transition-colors hover:bg-surface-variant"
    >
      <div className="flex items-start justify-between gap-2">
        <span className="line-clamp-2 text-data-md font-medium text-on-surface">{deal.title}</span>
        {typeof deal.ai_score === 'number' && (
          <Badge tone={deal.ai_score >= 70 ? 'success' : deal.ai_score >= 40 ? 'warning' : 'neutral'}>
            {deal.ai_score}%
          </Badge>
        )}
      </div>

      <div className="flex items-center justify-between">
        <span className="text-data-md font-semibold text-on-surface">
          {formatMoney(deal.value, deal.currency)}
        </span>
        {deal.priority && <Badge tone={PRIORITY_TONES[deal.priority] ?? 'neutral'}>{deal.priority}</Badge>}
      </div>

      {(deal.business_name || deal.assigned_name) && (
        <div className="flex items-center justify-between text-body-sm text-on-surface-variant">
          <span className="truncate">{deal.business_name || '—'}</span>
          <span className="truncate">{deal.assigned_name || ''}</span>
        </div>
      )}

      {deal.expected_close && (
        <span className="text-body-sm text-on-surface-variant">Close: {deal.expected_close}</span>
      )}

      {/* Quick stage move — reuses native select; does not open the drawer */}
      <select
        aria-label="Change stage"
        value={deal.stage}
        onClick={(e) => e.stopPropagation()}
        onChange={(e) => {
          e.stopPropagation();
          const next = e.target.value as DealStage;
          if (next !== deal.stage) onStageChange(deal, next);
        }}
        className="mt-1 h-8 w-full rounded-DEFAULT border border-outline bg-surface px-2 text-body-sm text-on-surface focus:outline-none focus:ring-2 focus:ring-primary/40"
      >
        {STAGE_OPTIONS.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </div>
  );
}
